import { z } from 'zod';
import { daysBetweenInclusive } from '../dates';
import {
  concurrency,
  currencyCode,
  emptyAsNull,
  emptyAsUndefined,
  exchangeRate,
  gstRatePercent,
  id,
  listQuery,
  moneyAmount,
  optionalId,
  optionalText,
  requiredId,
  ymd,
} from './common';
import { invoiceFields } from './invoice';

/**
 * Other (non-marketing) expenses: salary, servers, software, office…
 * `appId` empty = company-level expense (not tied to one app).
 * GST is tracked separately and never part of operating expense.
 */
export const otherExpenseCreateSchema = z.object({
  transactionDate: ymd,
  categoryId: requiredId('Category'),
  appId: optionalId,
  amountBeforeGst: moneyAmount,
  gstRate: gstRatePercent,
  currencyCode: emptyAsUndefined(currencyCode),
  /** Only for non-INR expenses when no monthly rate is configured. */
  exchangeRate: emptyAsUndefined(exchangeRate),
  gstAccountId: optionalId,
  ...invoiceFields,
  /** Save even though an identical expense exists (date, category, app, amount, invoice). */
  confirmDuplicate: z.boolean().optional(),
});
export type OtherExpenseCreateInput = z.output<typeof otherExpenseCreateSchema>;

export const otherExpenseUpdateSchema = z
  .object({
    transactionDate: ymd,
    categoryId: requiredId('Category'),
    appId: optionalId,
    amountBeforeGst: moneyAmount,
    gstRate: gstRatePercent,
    exchangeRate: emptyAsUndefined(exchangeRate),
    gstAccountId: optionalId,
    ...invoiceFields,
  })
  .partial()
  .extend(concurrency);
export type OtherExpenseUpdateInput = z.output<typeof otherExpenseUpdateSchema>;

const otherFilters = {
  startDate: ymd.optional(),
  endDate: ymd.optional(),
  categoryId: id.optional(),
  appId: id.optional(),
  /** Only company-level expenses (no app). */
  companyOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  gstAccountId: id.optional(),
};
export const otherExpenseListSchema = listQuery.extend(otherFilters);
export const otherExpenseSummarySchema = z.object(otherFilters);
export type OtherExpenseFilters = z.output<typeof otherExpenseSummarySchema>;

/** A row of the morning entry "Other expenses" section (INR only). */
export const morningOtherRowSchema = z.object({
  entryId: emptyAsUndefined(id),
  categoryId: requiredId('Category'),
  appId: emptyAsNull(id),
  amountBeforeGst: moneyAmount,
  gstRate: gstRatePercent,
  notes: optionalText(5000),
  updatedAt: z.iso.datetime({ offset: true }).optional(),
});
export type MorningOtherRow = z.output<typeof morningOtherRowSchema>;

/** Profit & loss for an inclusive date range (max ~3 years), optionally one app. */
export const pnlQuerySchema = z
  .object({ startDate: ymd, endDate: ymd, appId: id.optional() })
  .refine((r) => r.startDate <= r.endDate, {
    path: ['endDate'],
    message: 'End date must be on or after start date',
  })
  .refine((r) => daysBetweenInclusive(r.startDate, r.endDate) <= 1100, {
    path: ['endDate'],
    message: 'Range cannot exceed 1100 days',
  });
